import React, { useState, useEffect } from 'react';
import { fetchUpcomingTournaments, updateTournamentBooking } from '../api/tournamentApi';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Pagination,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { useNavigate } from 'react-router-dom';
import '../../src/TournamentList.css';

const TournamentList = () => {
  const [tournaments, setTournaments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [bookingStatus, setBookingStatus] = useState({});
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        setLoading(true);
        const data = await fetchUpcomingTournaments(currentPage);
        const reversedTournaments = data.response.data.reverse();
        setTournaments(reversedTournaments);
        setTotalPages(data.response.pagination.last_page);
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTournaments();
  }, [currentPage]);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handlePreBookClick = (tournament) => {
    if (tournament.is_pre_booked) {
      // If the tournament is already pre-booked, change it to booked by hitting the API with updated values
      setSelectedTournament(tournament);
      setCode('');
      setDiscount(0);
      handleSubmitBooking(tournament, false);
    } else {
      // If the tournament is not pre-booked, open the dialog to initiate pre-booking
      setSelectedTournament(tournament);
      setOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setCode('');
    setDiscount(0);
    setError('');
  };

  const handleSubmitBooking = async (tournament = selectedTournament, isPreBooked = true) => {

    try {
      const payload = {
        razorpay_script: isPreBooked ? code : '',
        discount: isPreBooked ? discount : 0,
        is_pre_booked: isPreBooked
      };


      const response = await updateTournamentBooking(tournament.id, payload);

      if (response.status) {
        setBookingStatus(prevStatus => ({
          ...prevStatus,
          [tournament.id]: isPreBooked ? 'Booked' : 'Available'
        }));

        // Update tournament's booking status after successful API call
        tournament.is_pre_booked = isPreBooked;

        // Close the dialog after successful submission
        handleCloseDialog();
      } else {
        alert('Booking failed. Please try again!');
      }
    } catch (error) {
      console.error('Booking API failed:', error);
      alert('An error occurred while booking the tournament.');
    }
  };

  const handleEditClick = (tournament) => {
    navigate('/create-tournament', { state: { tournament } });
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  return (
    <Box sx={{ padding: 4 }}>
      <h2>Tournaments List</h2>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleBackClick}
        sx={{ marginBottom: 2 }}
      >
        Back to Dashboard
      </Button>
      {loading ? (
        <CircularProgress />
      ) : tournaments.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 6,
            color: 'text.secondary',
          }}
        >
          <InboxIcon sx={{ fontSize: 96, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Tournaments Found
          </Typography>
          <Typography variant="body2">
            There are no tournaments to display right now.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {tournaments.map((tournament) => (
            <Grid item xs={12} md={6} key={tournament.id}>
              <Card>
                <CardContent>
                  <Typography variant="h5" component="div">
                    {tournament.unique_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    State: {tournament.state}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start Time: {new Date(tournament.start_time).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Format: {tournament.format}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Description: {tournament.desc || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Fee: {tournament.participation_fee_currency} {tournament.participation_fee}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prize Money: {tournament.min_prize_money}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Max Participants: {tournament.max_participants}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Joined Participants: {tournament.participants_count}
                  </Typography>
                </CardContent>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleEditClick(tournament)}
                  sx={{ marginTop: 2 }}
                >
                  Edit
                </Button>
                {/* <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handlePreBookClick(tournament)}
                  sx={{ marginTop: 2 }}
                >
                  {tournament.is_pre_booked || bookingStatus[tournament.id] === 'Booked' ? 'Booked' : 'Pre Book'}
                </Button> */}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      {tournaments.length > 0 && (
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          sx={{ marginTop: 2 }}
        />
      )}

      {/* Dialog for Pre Booking */}
      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>Pre Book Tournament</DialogTitle>
        <DialogContent>
          <TextField
            label="Booked Code"
            variant="outlined"
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            sx={{ marginBottom: 2 }}
            required  // Mark this field as required
            error={!code && open}  // Display error if the field is empty and dialog is open
            helperText={!code && open ? 'Booked Code is required' : ''}
          />
          <TextField
            label="Discount"
            variant="outlined"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            sx={{ marginBottom: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (code) {
                handleSubmitBooking(selectedTournament); // Proceed only if code is provided
              }
            }}
            color="primary"
            disabled={!code} // Disable the submit button if the code is not provided
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TournamentList;
